import React, { useState, useEffect, useCallback } from "react";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemText,
	TextField,
	Typography,
	CircularProgress,
} from "@mui/material";
import { DeleteOutlined, Edit, Check, Close } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles()((theme) => ({
	fila: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		paddingLeft: 0,
		paddingRight: 0,
	},
	campoNuevo: {
		display: "flex",
		gap: theme.spacing(1),
		alignItems: "flex-start",
		marginBottom: theme.spacing(1),
	},
	vacio: {
		color: theme.palette.text.secondary,
		fontSize: 13.5,
		padding: theme.spacing(2, 0),
		textAlign: "center",
	},
	conteoColas: {
		fontSize: 12.5,
		color: theme.palette.text.secondary,
		whiteSpace: "nowrap",
	},
	cargando: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(3, 0),
	},
}));

const QueueGroupModal = ({ open, onClose }) => {
	const { classes } = useStyles();

	const [grupos, setGrupos] = useState([]);
	const [cargando, setCargando] = useState(false);
	const [nombreNuevo, setNombreNuevo] = useState("");
	const [guardando, setGuardando] = useState(false);
	const [editandoId, setEditandoId] = useState(null);
	const [nombreEditado, setNombreEditado] = useState("");
	const [porBorrar, setPorBorrar] = useState(null);

	const cargar = useCallback(async () => {
		setCargando(true);
		try {
			const { data } = await api.get("/queue-group");
			setGrupos(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		} finally {
			setCargando(false);
		}
	}, []);

	useEffect(() => {
		if (open) {
			cargar();
			setNombreNuevo("");
			setEditandoId(null);
		}
	}, [open, cargar]);

	const handleCrear = async () => {
		const nombre = nombreNuevo.trim();
		if (nombre.length < 2) return;
		setGuardando(true);
		try {
			await api.post("/queue-group", { name: nombre });
			setNombreNuevo("");
			toast.success(i18n.t("queueGroupModal.toasts.created"));
			await cargar();
		} catch (err) {
			toastError(err);
		} finally {
			setGuardando(false);
		}
	};

	const handleRenombrar = async (id) => {
		const nombre = nombreEditado.trim();
		if (nombre.length < 2) return;
		setGuardando(true);
		try {
			await api.put(`/queue-group/${id}`, { name: nombre });
			setEditandoId(null);
			toast.success(i18n.t("queueGroupModal.toasts.updated"));
			await cargar();
		} catch (err) {
			toastError(err);
		} finally {
			setGuardando(false);
		}
	};

	const handleBorrar = async () => {
		if (!porBorrar) return;
		try {
			await api.delete(`/queue-group/${porBorrar.id}`);
			toast.success(i18n.t("queueGroupModal.toasts.deleted"));
			await cargar();
		} catch (err) {
			toastError(err);
		} finally {
			setPorBorrar(null);
		}
	};

	return (
		<>
			<ConfirmationModal
				title={i18n.t("queueGroupModal.confirmDelete.title")}
				open={Boolean(porBorrar)}
				onClose={() => setPorBorrar(null)}
				onConfirm={handleBorrar}
			>
				{i18n.t("queueGroupModal.confirmDelete.message")}
			</ConfirmationModal>

			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="paper">
				<DialogTitle>{i18n.t("queueGroupModal.title")}</DialogTitle>
				<DialogContent dividers>
					<div className={classes.campoNuevo}>
						<TextField
							label={i18n.t("queueGroupModal.form.name")}
							value={nombreNuevo}
							onChange={(e) => setNombreNuevo(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleCrear();
								}
							}}
							variant="outlined"
							margin="dense"
							size="small"
							fullWidth
							autoFocus
						/>
						<Button
							variant="contained"
							color="primary"
							onClick={handleCrear}
							disabled={guardando || nombreNuevo.trim().length < 2}
							style={{ marginTop: 8 }}
						>
							{i18n.t("queueGroupModal.buttons.add")}
						</Button>
					</div>

					{cargando ? (
						<div className={classes.cargando}>
							<CircularProgress size={22} />
						</div>
					) : grupos.length === 0 ? (
						<Typography className={classes.vacio}>
							{i18n.t("queueGroupModal.empty")}
						</Typography>
					) : (
						<List dense>
							{grupos.map((grupo) => (
								<ListItem key={grupo.id} className={classes.fila} divider>
									{editandoId === grupo.id ? (
										<>
											<TextField
												value={nombreEditado}
												onChange={(e) => setNombreEditado(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === "Enter") {
														e.preventDefault();
														handleRenombrar(grupo.id);
													}
													if (e.key === "Escape") setEditandoId(null);
												}}
												variant="outlined"
												size="small"
												fullWidth
												autoFocus
											/>
											<IconButton
												size="small"
												onClick={() => handleRenombrar(grupo.id)}
												disabled={guardando}
											>
												<Check fontSize="small" />
											</IconButton>
											<IconButton size="small" onClick={() => setEditandoId(null)}>
												<Close fontSize="small" />
											</IconButton>
										</>
									) : (
										<>
											<ListItemText primary={grupo.name} />
											<span className={classes.conteoColas}>
												{grupo.queues ? grupo.queues.length : 0}{" "}
												{i18n.t("queueGroupModal.queues")}
											</span>
											<IconButton
												size="small"
												onClick={() => {
													setEditandoId(grupo.id);
													setNombreEditado(grupo.name);
												}}
											>
												<Edit fontSize="small" />
											</IconButton>
											<IconButton size="small" onClick={() => setPorBorrar(grupo)}>
												<DeleteOutlined fontSize="small" />
											</IconButton>
										</>
									)}
								</ListItem>
							))}
						</List>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} color="secondary" variant="outlined">
						{i18n.t("queueGroupModal.buttons.close")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default QueueGroupModal;
